import { useState } from "react";
import Box from "@mui/material/Box";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TextField from "../Form/TextField";
import IconButton from "../Buttons/IconButton";
import { SimpleAlert } from "../Alerts/Alerts";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startUpdateAdsUserPassword } from "../../actions/user";
import { REGEX_PASSWORD } from "../../utils/regex";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import {
  BUTTON_CLOSE,
  BUTTON_SAVE,
  FIELD_PASSWORD_INSECURE,
  FIELD_PASSWORDS_NO_MATCH,
  FIELD_REQUIRED,
  PASSWORD_CHANGED,
  PASSWORD_CONFIRMATION_PLACEHOLDER,
  PASSWORD_PLACEHOLDER,
  PASSWORD_TITLE,
} from "../../i18n/keysTranslations";

const ModalChangePassword = ({ modalOpen, onCloseModal }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { control, formState, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  useFocusErrorForm(formState);
  const { errors } = formState;

  const onToggleShowPassword = () => setShowPassword((prev) => !prev);

  const onClose = () => {
    reset();
    setShowPassword(false);
    onCloseModal();
  };

  const onSubmit = async ({ password }) => {
    setIsFetching(true);
    const response = await dispatch(startUpdateAdsUserPassword({ password }));
    if (response) {
      await SimpleAlert({
        title: t(PASSWORD_CHANGED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      onClose();
    }
    setIsFetching(false);
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      fullWidth={true}
      maxWidth="xs"
      scroll="paper"
      ariaLabel={t(PASSWORD_TITLE)}
      title={t(PASSWORD_TITLE)}
      sxTitle={{ color: "background.paperContrastText" }}
    >
      <Box
        onSubmit={handleSubmit(onSubmit)}
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <TextField
          fullWidth
          id="change-password"
          name="password"
          label={t(PASSWORD_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onToggleShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(FIELD_PASSWORD_INSECURE);
              }
            },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          id="change-password-confirm"
          name="confirmPassword"
          label={t(PASSWORD_CONFIRMATION_PLACEHOLDER)}
          type={showPassword ? "text" : "password"}
          variant="filled"
          color="primary"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={onToggleShowPassword}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
          control={control}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              const password = watch("password");
              if (value !== password) {
                return t(FIELD_PASSWORDS_NO_MATCH);
              }
            },
          }}
          errors={errors}
        />
        <ContainerModalActions
          hasPadding={false}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            loading: isFetching,
            onClick: () => {},
          }}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CLOSE,
            loading: isFetching,
            onClick: onClose,
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalChangePassword;
